import React, { useState } from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';
import TransactionDemo from './TransactionDemo';

const GameOverContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 30px;
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 700px;
  margin: 20px auto;
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  animation: slideIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const GameOverTitle = styled.h2`
  font-size: 42px;
  background: linear-gradient(90deg, #14F195, #9945FF);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 25px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
`;

const GameOverScore = styled.div`
  font-size: 80px;
  font-weight: 900;
  background: linear-gradient(45deg, #9945FF, #14F195);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 20px 0 30px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  position: relative;
  padding: 20px 40px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::before {
    content: "MAXIMUM SCORE";
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
    background: linear-gradient(45deg, #9945FF, #14F195);
    color: white;
    padding: 5px 20px;
    border-radius: 30px;
    font-weight: 700;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    -webkit-text-fill-color: white;
  }
`;

const Medal = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 50px;
  margin-bottom: 30px;
  box-shadow: 0 10px 30px rgba(255, 193, 7, 0.4);
  position: relative;
  animation: pulse 2s infinite;
  
  &::before {
    content: "🏆";
    position: relative;
    z-index: 2;
  }
  
  &::after {
    content: "";
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.5), rgba(255, 165, 0, 0.5));
    border-radius: 50%;
    z-index: 1;
    filter: blur(10px);
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const GameOverText = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 30px;
  line-height: 1.8;
  max-width: 600px;
  font-weight: 500;
`;

const PlayAgainButton = styled.button`
  background: linear-gradient(45deg, #9945FF, #14F195);
  color: white;
  font-size: 18px;
  padding: 15px 35px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 20px;
  box-shadow: 0 8px 25px rgba(153, 69, 255, 0.3);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(153, 69, 255, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const ShareContainer = styled.div`
  margin-top: 40px;
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const ShareTitle = styled.h3`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  font-weight: 600;
`;

const ShareButton = styled.button`
  background: linear-gradient(45deg, #1da1f2, #0d8bd9);
  color: white;
  font-size: 16px;
  padding: 14px 25px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(29, 161, 242, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  gap: 10px;
  position: relative;
  overflow: hidden;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(29, 161, 242, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const Confetti = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: ${() => {
    const colors = ['#f44336', '#2196f3', '#ffeb3b', '#4caf50', '#9c27b0', '#ff9800', '#14F195', '#9945FF'];
    return colors[Math.floor(Math.random() * colors.length)];
  }};
  border-radius: ${() => (Math.random() > 0.5 ? '50%' : '4px')};
  animation: confetti 5s ease-in infinite;
  transform: rotate(${() => Math.random() * 360}deg);
  opacity: ${() => Math.random()};
  
  &:nth-child(odd) {
    animation-delay: ${() => Math.random() * 3}s;
  }
  
  @keyframes confetti {
    0% {
      transform: translateY(-300px) rotate(0);
      opacity: 1;
    }
    100% {
      transform: translateY(1000px) rotate(720deg);
      opacity: 0;
    }
  }
`;

const DividerText = styled.div`
  margin: 40px 0;
  font-size: 16px;
  position: relative;
  text-align: center;
  width: 100%;
  color: rgba(255, 255, 255, 0.6);
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 35%;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &::before {
    left: 0;
  }
  
  &::after {
    right: 0;
  }
`;

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L17.306 2.25h.938Z" />
  </svg>
);

const ClaimRewardButton = styled.button`
  background: linear-gradient(45deg, #FFD700, #FFA500);
  color: white;
  font-size: 16px;
  padding: 14px 25px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(255, 215, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 0;
  gap: 10px;
  position: relative;
  overflow: hidden;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(255, 215, 0, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const CoinIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,10.5A1.5,1.5 0 0,1 13.5,12A1.5,1.5 0 0,1 12,13.5A1.5,1.5 0 0,1 10.5,12A1.5,1.5 0 0,1 12,10.5Z" />
  </svg>
);

const renderConfetti = () => {
  const confettiCount = 100;
  const confetti = [];
  
  for (let i = 0; i < confettiCount; i++) {
    confetti.push(
      <Confetti
        key={i}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDuration: `${3 + Math.random() * 5}s`,
        }}
      />
    );
  }
  
  return confetti;
};

const GameOver: React.FC = () => {
  const { creditScore, resetGame } = useGame();
  const [showTransaction, setShowTransaction] = useState(false);
  
  const handleShare = () => {
    const shareText = `I reached maximum Tariff Score of ${creditScore} on @tariffscore! Can you beat it?`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank');
  };

  return (
    <GameOverContainer>
      {renderConfetti()}
      <Medal />
      <GameOverTitle>Tariff Score</GameOverTitle>
      <GameOverScore>{creditScore}</GameOverScore>
      <GameOverText>
        Congratulations! You've answered all the tariff questions and achieved the maximum score. 
        Your understanding of tariff policy is impressive!
      </GameOverText>
      
      <PlayAgainButton onClick={resetGame}>
        <RefreshIcon />
        Play Again
      </PlayAgainButton>
      
      <ClaimRewardButton onClick={() => setShowTransaction(true)}>
        <CoinIcon />
        Claim SOL Reward
      </ClaimRewardButton>
      
      <DividerText>OR</DividerText>
      
      <ShareContainer>
        <ShareTitle>Share your achievement with the world!</ShareTitle>
        <ShareButton onClick={handleShare}>
          Share on X
        </ShareButton>
      </ShareContainer>
      
      {showTransaction && <TransactionDemo rewardAmount={0.001} onClose={() => setShowTransaction(false)} />}
    </GameOverContainer>
  );
};

export default GameOver; 